import type { AddToProductImage } from '../types'

export type SlotMap = Record<number, AddToProductImage>

export type ShiftPlan =
  | {
      kind: 'append'
      placements: [{ slot: number; image: AddToProductImage }]
    }
  | {
      kind: 'insert'
      placements: [{ slot: number; image: AddToProductImage }]
      shifted: Array<{ from: number; to: number }>
    }
  | {
      kind: 'reject'
      reason: 'same_position'
    }

/**
 * Plans the placement of `newImage` into `targetSlot` on a slot-indexed map.
 *
 * Rules (see Bundle-C contract):
 * - Reject when the same image is dropped on the slot it already occupies.
 * - Append (no shift) when the target slot is empty or beyond `maxOccupied`.
 * - Insert + shift: every occupied slot ≥ target moves up by one. Gaps are
 *   preserved — we never compact occupants into them.
 */
export function slotInsert(
  current: SlotMap,
  targetSlot: number,
  newImage: AddToProductImage,
): ShiftPlan {
  if (current[targetSlot]?.id === newImage.id) {
    return { kind: 'reject', reason: 'same_position' }
  }
  if (!current[targetSlot]) {
    return { kind: 'append', placements: [{ slot: targetSlot, image: newImage }] }
  }
  const occupiedFromTarget = Object.keys(current)
    .map(Number)
    .filter((n) => Number.isFinite(n) && n >= targetSlot && current[n])
    .sort((a, b) => a - b)
  return {
    kind: 'insert',
    placements: [{ slot: targetSlot, image: newImage }],
    shifted: occupiedFromTarget.map((s) => ({ from: s, to: s + 1 })),
  }
}
